/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { ChangeEvent, Fragment, ReactElement, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import RequestHandler from '../../../lib/request_handler'
import Page from '../../Commons/Page'
import { FeaturedArticle } from '../../types'
import routes from '../../utils/routes'

type Props = {
  article: FeaturedArticle
}

type ArticleFormProps = {
  title: string | null
  kind: string | null
  publishedAt: string | null
  appTopBanner?: File
  spwebLeafletBanner?: File
}

type ContentFormProps = {
  topVisualImage?: File
  lead: string | null
  leadTextColor: string | null
  description: string | null
}

type FormProps = ArticleFormProps | ContentFormProps

type SnackBarState = {
  open: boolean
  message: string
  severity: 'success' | 'error'
}

function usePreviewFile(): [string, (arg: File) => void] {
  const [url, setUrl] = useState('')
  const changeUrlByFile = (file: File) => {
    setUrl(URL.createObjectURL(file))
  }

  return [url, changeUrlByFile]
}

function Show(props: Props): ReactElement {
  const article = props.article
  const [appTopBannerUrl, changeAppTopBannerUrlByFile] = usePreviewFile()
  const [spwebLeafletBannerUrl, changeSpwebLeafletBannerUrlByFile] = usePreviewFile()
  const [topVisualImageUrl, changeTopVisualImageUrlByFile] = usePreviewFile()
  const [snackbarState, setSnackbarState] = useState<SnackBarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  // 初期化時にnullが設定されているとそのまま送信したときに'null'という文字列で送信されてしまう
  // フロントエンド側の課題なのでフロントエンドでハンドリングする
  const { register, handleSubmit, setValue, watch, control } = useForm<FormProps>({
    shouldUnregister: false,
    defaultValues: {
      title: article.title || '',
      kind: article.kind,
      publishedAt: article.publishedAt || '',
      lead: article.content.lead || '',
      leadTextColor: article.content.leadTextColor || '',
      description: article.content.description || '',
    },
  })
  const watchTitle = watch('title')

  const onDeleteArticle = async () => {
    if (!confirm('本当に削除しますか?')) {
      return
    }

    const handler = new RequestHandler(
      routes.staff_admin_featured_article_path(article.id),
      'delete'
    )
    try {
      await handler.request()
      location.href = routes.staff_admin_featured_articles_path()
    } catch {
      setSnackbarState({ message: '削除に失敗しました', open: true, severity: 'error' })
    }
  }

  const onSubmit = async (data: FormProps) => {
    const handler = new RequestHandler(
      routes.staff_admin_featured_article_path(article.id),
      'PUT',
      data,
      true
    )
    try {
      await handler.requestWithBody()
      setSnackbarState({ message: '保存しました', open: true, severity: 'success' })
    } catch {
      setSnackbarState({ message: '保存に失敗しました', open: true, severity: 'error' })
    }
  }

  const onAppTopBannerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }

    setValue('appTopBanner', file)
    changeAppTopBannerUrlByFile(file)
  }

  const onSpwebLeafletBannerChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }

    setValue('spwebLeafletBanner', file)
    changeSpwebLeafletBannerUrlByFile(file)
  }

  const onTopVisualImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0]
    if (!file) {
      return
    }

    setValue('topVisualImage', file)
    changeTopVisualImageUrlByFile(file)
  }

  return (
    <Fragment>
      <Page>
        <Typography variant="h3" sx={{ my: 2 }}>
          <Link href={routes.root_path}>特集</Link> / {watchTitle || 'タイトル未設定'}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Box width="800px">
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 4 }}>
              <Tabs
                value={1}
                onChange={() =>
                  (location.href = routes.staff_admin_featured_article_content_path(article.id))
                }
                aria-label="basic tabs example"
              >
                <Tab label="特集" value={1} />
                <Tab label="コンテンツ本文" value={2} />
              </Tabs>
            </Box>
            <form
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <Stack spacing={2}>
                <Box>
                  <Box>
                    <Typography variant="caption">ID</Typography>
                  </Box>
                  <Typography variant="body1">{article.id}</Typography>
                </Box>
                <TextField label="タイトル" fullWidth {...register('title')} margin="normal" />
                <Box>
                  <Box>
                    <Typography variant="caption">種別</Typography>
                    <Controller
                      name="kind"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup row {...field}>
                          <FormControlLabel value="food" control={<Radio />} label="食品" />
                          <FormControlLabel value="life" control={<Radio />} label="生活" />
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box>
                    <Typography variant="caption">公開日時</Typography>
                  </Box>
                  <Box sx={{ width: 200 }}>
                    <input
                      css={css`
                        height: 40px;
                      `}
                      type={process.env.RAILS_ENV === 'test' ? 'text' : 'datetime-local'}
                      {...register('publishedAt')}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box>
                    <Typography variant="caption">アプリトップバナー画像</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <input type="file" onChange={onAppTopBannerChange} name="appTopBanner" />
                  </Box>
                  {appTopBannerUrl ? (
                    <img src={appTopBannerUrl} css={{ width: 300 }} />
                  ) : (
                    article.appTopBanner && (
                      <img src={article.appTopBanner.url} css={{ width: 300 }} />
                    )
                  )}
                </Box>
                <Box>
                  <Box>
                    <Typography variant="caption">SPWebチラシバナー画像</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <input
                      type="file"
                      onChange={onSpwebLeafletBannerChange}
                      name="spwebLeafletBanner"
                    />
                  </Box>
                  {spwebLeafletBannerUrl ? (
                    <img src={spwebLeafletBannerUrl} css={{ width: 300 }} />
                  ) : (
                    article.spwebLeafletBanner && (
                      <img src={article.spwebLeafletBanner.url} css={{ width: 300 }} />
                    )
                  )}
                </Box>
                <Divider />
                <Box>
                  <Typography variant="h5">特集コンテンツ</Typography>
                </Box>
                <Box>
                  <Box>
                    <Typography variant="caption">トップビジュアル</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <input type="file" onChange={onTopVisualImageChange} name="topVisualImage" />
                  </Box>
                  {topVisualImageUrl ? (
                    <img src={topVisualImageUrl} css={{ width: 300 }} />
                  ) : (
                    article.content.topVisualImage && (
                      <img src={article.content.topVisualImage.url} css={{ width: 300 }} />
                    )
                  )}
                  <TextField
                    label="リード文"
                    fullWidth
                    {...register('lead')}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="リード文テキスト色"
                    placeholder="#000000"
                    {...register('leadTextColor')}
                    margin="normal"
                  />
                  <TextField
                    label="ディスクリプション"
                    fullWidth
                    {...register('description')}
                    margin="normal"
                    multiline
                    rows={4}
                  />
                </Box>
              </Stack>
              <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <Button variant="contained" color="error" sx={{ mr: 2 }} onClick={onDeleteArticle}>
                  削除
                </Button>
                <Button type="submit" variant="contained">
                  保存
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Page>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarState({ ...snackbarState, open: false })
        }}
      >
        <Alert severity={snackbarState.severity} sx={{ width: '100%' }} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Fragment>
  )
}

export default Show
